import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import {
  Star, ChevronLeft, ChevronRight, ShoppingCart, Search, Megaphone, Share2, Smartphone, MonitorSmartphone, PenTool,
  LayoutTemplate, Globe, Code2, AppWindow, Workflow, ShieldCheck, Gauge, Wrench, Headset, Lightbulb, Cpu, Boxes,
  FileText, Layers, Building2, Briefcase,
} from "lucide-react";
import { testimonials } from "@/content/testimonials";
import { SectionHead, ArrowLink } from "@/components/common/Bits";

const icons = { ShoppingCart, Search, Megaphone, Share2, Smartphone, MonitorSmartphone, PenTool, LayoutTemplate, Globe, Code2, AppWindow, Workflow, ShieldCheck, Gauge, Wrench, Headset, Lightbulb, Cpu, Boxes, FileText, Layers, Building2, Briefcase };

export const Stars = ({ n = 5 }) => (
  <span className="flex gap-0.5" role="img" aria-label={`${n} out of 5 stars`}>
    {Array.from({ length: 5 }, (_, i) => <Star key={i} className={`h-4 w-4 ${i < n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} aria-hidden />)}
  </span>
);

// Equal-height review card. Icon tile replaces a profile photo.
export const ReviewCard = ({ t, className = "" }) => {
  const I = icons[t.icon] || Briefcase;
  return (
    <figure className={`flex h-full flex-col rounded-2xl border border-line bg-white p-6 sm:p-7 ${className}`} data-testid="review-card">
      <Stars n={t.stars} />
      <blockquote className="mt-4 flex-1 text-[15px] leading-relaxed text-slate-700">{t.text}</blockquote>
      <figcaption className="mt-6 flex items-center gap-3 border-t border-line pt-5">
        <span className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-full bg-teal/10 text-teal" aria-hidden><I className="h-5 w-5" strokeWidth={1.8} /></span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-bold text-ink">{t.name}</span>
          <span className="block truncate text-xs font-semibold text-slate-500">{t.service}</span>
        </span>
      </figcaption>
    </figure>
  );
};

export const Testimonials = () => {
  const [ref, api] = useEmblaCarousel({ loop: true, align: "start", skipSnaps: false });
  const [paused, setPaused] = useState(false);
  const prev = useCallback(() => api?.scrollPrev(), [api]);
  const next = useCallback(() => api?.scrollNext(), [api]);

  useEffect(() => {
    if (!api || paused) return undefined;
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    const id = setInterval(() => api.scrollNext(), 6000);
    return () => clearInterval(id);
  }, [api, paused]);

  return (
    <section className="bg-white py-20 sm:py-24 lg:py-28" aria-labelledby="reviews-title" data-testid="testimonials-section">
      <div className="container-x">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-6 lg:mb-12">
          <SectionHead eyebrow="Customer reviews" title="What our customers say." lede="Five-star feedback from businesses we have worked with." className="!mb-0" />
          <div className="flex items-center gap-3">
            <button type="button" onClick={prev} aria-label="Previous review" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-line hover:border-ink/40" data-testid="reviews-prev"><ChevronLeft className="h-5 w-5" aria-hidden /></button>
            <button type="button" onClick={next} aria-label="Next review" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-line hover:border-ink/40" data-testid="reviews-next"><ChevronRight className="h-5 w-5" aria-hidden /></button>
          </div>
        </div>
        <h2 id="reviews-title" className="sr-only">Customer reviews</h2>
        <div ref={ref} className="overflow-hidden" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocusCapture={() => setPaused(true)} onBlurCapture={() => setPaused(false)} role="region" aria-roledescription="carousel" aria-label="Customer reviews">
          <div className="flex items-stretch">
            {testimonials.map((t) => (
              <div key={t.name} className="min-w-0 flex-[0_0_100%] pr-4 sm:flex-[0_0_50%] lg:flex-[0_0_33.3333%]" role="group" aria-roledescription="slide" aria-label={`${t.name}, ${t.service}`}>
                <ReviewCard t={t} />
              </div>
            ))}
          </div>
        </div>
        <ArrowLink to="/reviews" className="mt-10" testid="all-reviews-link">Read all customer reviews</ArrowLink>
      </div>
    </section>
  );
};
